import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { CheckCircle2, Truck, Clock, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import useSeo from "@/lib/useSeo";
import { inr, waLink } from "@/lib/content";

const PM = { cod: "Cash on Delivery", upi: "UPI QR" };
const STATUS = { pending_payment: "Awaiting payment", placed: "Payment verification pending", expired: "Expired – not paid", confirmed: "Confirmed", shipped: "Shipped", payment_failed: "Payment not received" };
const BANNERS = {
  confirmed: ["bg-[#1B4332]", "Order Confirmed!", "Your UPI payment is verified. We're packing your Kurtaani outfit for dispatch."],
  shipped: ["bg-[#1B4332]", "Your order has shipped", "Your Kurtaani parcel is on its way. Expected delivery:"],
  payment_failed: ["bg-red-700", "Payment not received", "We couldn't find a payment for this UTR. Please WhatsApp us with your payment screenshot and we'll sort it out."],
};

const StatusBanner = ({ o }) => {
  const [bg, title, text] = BANNERS[o.status];
  return (
    <div data-testid={`order-banner-${o.status}`} className={`mt-8 rounded-3xl p-6 text-[#FAF7F2] sm:p-8 ${bg}`}>
      <p className="flex items-center gap-2 font-serif-display text-3xl">{o.status === "payment_failed" ? <MessageCircle className="h-6 w-6" /> : <CheckCircle2 className="h-6 w-6" />} {title}</p>
      <p className="mt-2 text-sm text-white/85">{text} {o.status === "shipped" && <b>{o.estimated_delivery}</b>}</p>
      {o.status === "payment_failed" && <a href={waLink(`Hi Kurtaani, my order ${o.order_number} shows payment not received (UTR ${o.utr}).`)} target="_blank" rel="noreferrer" className="btn-light mt-4">WhatsApp us</a>}
    </div>
  );
};

const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

const ConfirmationCountdown = ({ seconds, order }) => {
  const [left, setLeft] = useState(seconds);
  useEffect(() => {
    setLeft(seconds);
    const t = setInterval(() => setLeft((s) => Math.max(s - 1, 0)), 1000);
    return () => clearInterval(t);
  }, [seconds]);
  const done = left <= 0;
  return (
    <div data-testid="confirmation-countdown" className="mt-8 rounded-3xl bg-[#701A24] p-6 text-[#FAF7F2] sm:p-8">
      <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#D99B8E]"><Clock className="h-4 w-4" /> Payment verification in progress</p>
      {done ? (
        <>
          <p data-testid="confirmation-timer-done" className="mt-3 font-serif-display text-3xl">Almost there…</p>
          <p className="mt-2 text-sm text-[#EAE0D5]/85">Verification is taking a little longer than usual. Your order is safe – chat with us and we'll confirm it right away.</p>
          <a data-testid="confirmation-whatsapp-button" href={waLink(`Hi Kurtaani, please confirm my order ${order.order_number} (UTR ${order.utr}).`)} target="_blank" rel="noreferrer" className="btn-light mt-5"><MessageCircle className="h-4 w-4" /> WhatsApp us</a>
        </>
      ) : (
        <>
          <p data-testid="confirmation-timer" className="mt-3 font-serif-display text-6xl tabular-nums sm:text-7xl">{fmt(left)}</p>
          <p className="mt-2 text-sm text-[#EAE0D5]/85">We've received your UTR <b className="text-white">{order.utr}</b>. You'll get your order confirmation message within 30 minutes. You can refresh or close this page – your timer keeps running.</p>
        </>
      )}
    </div>
  );
};

export const OrderView = ({ o }) => (
  <div data-testid="order-details" className="rounded-3xl border border-[#E7E2DB] bg-white p-6 sm:p-8">
    <div className="flex flex-wrap justify-between gap-3 text-sm">
      <span>Order <b data-testid="order-number">{o.order_number}</b></span>
      <span className="rounded-full bg-[#1B4332] px-3 py-1 text-xs font-semibold capitalize text-white" data-testid="order-status">{STATUS[o.status] || o.status}</span>
    </div>
    <p className="mt-4 flex items-center gap-2 text-sm"><Truck className="h-4 w-4 text-[#B85D43]" /> Estimated delivery: <b>{o.estimated_delivery}</b></p>
    <div className="mt-5 space-y-3 border-t border-[#E7E2DB] pt-5">
      {o.items.map((i) => (
        <div key={i.slug + i.size} className="flex items-center gap-3 text-sm">
          <img src={i.image} alt={i.name} className="h-16 w-12 rounded-lg object-cover" />
          <span className="flex-1">{i.name}<span className="block text-xs text-[#78716C]">{i.size} · {i.color} · Qty {i.qty}</span></span>
          <span>{inr(i.line_total)}</span>
        </div>
      ))}
    </div>
    <div className="mt-5 flex justify-between border-t border-[#E7E2DB] pt-4 text-sm"><span>{PM[o.payment_method]}{o.utr ? ` · UTR ${o.utr}` : ""}</span><b data-testid="order-total">{inr(o.total)}</b></div>
    <p className="mt-2 text-xs text-[#78716C]">Delivering to {o.customer.name}, {o.customer.city} – {o.customer.pincode}</p>
  </div>
);

export default function OrderSuccess() {
  useSeo("Order Confirmed", null);
  const { orderNumber } = useParams();
  const [o, setO] = useState(null);
  useEffect(() => {
    const load = () => api.get(`/orders/${orderNumber}`).then((r) => setO(r.data));
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [orderNumber]);
  return (
    <div data-testid="order-success-page" className="kt-container max-w-2xl py-14 sm:py-20">
      <CheckCircle2 className="h-12 w-12 text-[#1B4332]" />
      <h1 className="mt-5 font-serif-display text-5xl font-semibold">Thank you{o ? `, ${o.customer.name.split(" ")[0]}` : ""}!</h1>
      <p className="mt-3 text-[#57534E]">Your Kurtaani order has been placed. Save your order number to track it anytime.</p>
      {o?.status === "placed" && o.confirmation_seconds_left !== null && <ConfirmationCountdown key={o.confirmation_seconds_left > 0 ? "run" : "done"} seconds={o.confirmation_seconds_left} order={o} />}
      {o && ["confirmed", "shipped", "payment_failed"].includes(o.status) && <StatusBanner o={o} />}
      <div className="mt-8">{o && <OrderView o={o} />}</div>
      <Link to="/collections/new-arrivals" className="btn-primary mt-8">Continue Shopping</Link>
    </div>
  );
}
